/**
 * Unified Archive Container Types
 *
 * The ArchiveContainer is the universal selection type for the workspace.
 * Any archive item (conversation, message, post, media, document) can be
 * represented as a container and displayed in the workspace.
 *
 * This unifies:
 * - ChatGPT/Claude conversations and messages
 * - Facebook posts, comments, and media
 * - Book chapters, passages, and threads
 * - Filesystem documents (txt, md, docx, pdf)
 *
 * URI Patterns:
 * - archive://chatgpt/{conversationId}
 * - archive://chatgpt/{conversationId}/message/{messageId}
 * - archive://facebook/post/{postId}
 * - archive://facebook/comment/{commentId}
 * - archive://facebook/media/{mediaId}
 * - archive://book/{bookId}/chapter/{chapterId}
 * - archive://book/{bookId}/passage/{passageId}
 * - fs://{path}
 */
// ═══════════════════════════════════════════════════════════════════
// CONTAINER FACTORIES
// ═══════════════════════════════════════════════════════════════════
/**
 * Generate a container URI
 */
export function generateContainerURI(type, source, id, parentId) {
    if (source === 'filesystem') {
        return `fs://${id}`;
    }
    if (parentId) {
        return `archive://${source}/${parentId}/${type}/${id}`;
    }
    return `archive://${source}/${type}/${id}`;
}
/**
 * Parse a container URI
 */
export function parseContainerURI(uri) {
    // Filesystem pattern: fs://path/to/file
    if (uri.startsWith('fs://')) {
        return {
            source: 'filesystem',
            type: 'document',
            id: uri.slice(5),
        };
    }
    // Archive pattern: archive://source/type/id or archive://source/parentId/type/id
    const archiveMatch = uri.match(/^archive:\/\/([^/]+)\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);
    if (archiveMatch) {
        const [, source, second, third, fourth] = archiveMatch;
        if (fourth) {
            // Has parent: archive://source/parentId/type/id
            return {
                source: source,
                type: third,
                id: fourth,
                parentId: second,
            };
        }
        // No parent: archive://source/type/id
        return {
            source: source,
            type: second,
            id: third,
        };
    }
    return null;
}
/**
 * Create default view hints based on container type
 */
export function getDefaultViewHints(type, contentType) {
    switch (type) {
        case 'media':
            return {
                preferredView: 'media',
                allowEdit: false,
                hasMetadataModal: true,
            };
        case 'conversation':
            return {
                preferredView: 'markdown',
                allowEdit: false,
                hasMetadataModal: true,
                collapsible: true,
            };
        case 'chapter':
        case 'passage':
            return {
                preferredView: 'book',
                allowEdit: true,
                hasMetadataModal: true,
            };
        case 'document':
            return {
                preferredView: contentType === 'markdown' ? 'markdown' : 'text',
                allowEdit: true,
                hasMetadataModal: true,
            };
        default:
            return {
                preferredView: 'markdown',
                allowEdit: true,
                hasMetadataModal: false,
            };
    }
}
/**
 * Check if container content needs preprocessing
 */
export function needsPreprocessing(content) {
    // Check for ChatGPT-style LaTeX delimiters
    if (/\\\[|\\\]|\\\(|\\\)/.test(content)) {
        return true;
    }
    // Check for embedded JSON artifacts
    if (/```json\s*\n\s*\{[\s\S]*?"type"\s*:/.test(content)) {
        return true;
    }
    // Check for thinking blocks
    if (/<thinking>|<antThinking>/.test(content)) {
        return true;
    }
    return false;
}
//# sourceMappingURL=container.js.map